import { faBars, faUser, faTriangleExclamation, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { User } from '@readcloud/data';
import {
  Avatar,
  AvatarFallback,
  Button,
  Menubar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Subtle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@readcloud/ui';
import packageJson from 'package.json';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCloudsAndContentActions } from '../context/ActionsContext';
import { useIsOnline, verifyOnlineStatus, uuidV4 } from '@readcloud/common';
import { jobsActions, useAppDispatch } from '@readcloud/state';
import { BookListLayout, CloudWithBooks, CreateCloudOperation } from '../types';
import { CloudsDropdown } from './CloudsDropdown';
import { ReactComponent as RCLogo } from './rc_logo_gradient.svg';
import { Grip } from 'lucide-react';
import {getSubdomainAssets} from "@readcloud/common/lib/utils/utils";

interface NavProps {
  navOpen?: boolean;
  onToggleSettings?: () => void;
  permissions?: {};
  clouds: CloudWithBooks[];
  knownUsers: User[];
  onRefresh: () => void;
  refreshing: boolean;
  layout: BookListLayout;
  onLayoutChange: (style: 'list' | 'grid') => void;
  onCloudSelected: (cloudId: string) => void;
  currentUserId: string;
  onCreateCloud: CreateCloudOperation;
  experimentalDesign?: boolean;
  userFullName: string;
  userEmail?: string;
}

// Check if running in Tauri environment
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

export const TopNavBar = (props: NavProps) => {
  const actions = useCloudsAndContentActions();
  const dispatch = useAppDispatch();
  const isOnline = useIsOnline();
  const [checkingOnline, setCheckingOnline] = useState(false);

  const handleRefreshWithOnlineCheck = async () => {
    setCheckingOnline(true);
    try {
      const online = await verifyOnlineStatus();
      if (online) {
        props.onRefresh();
      } else {
        dispatch(
          jobsActions.addJob({
            id: uuidV4(),
            name: 'Still offline',
            description: 'Could not connect to the server. Please check your internet connection.',
          })
        );
      }
    } finally {
      setCheckingOnline(false);
    }
  };

  const logo = useMemo(() => {
    const {logo} = getSubdomainAssets();
    if(!logo){
      return <RCLogo height={40} className='rc-max-w-[3rem] sm:rc-max-w-full'/>
    }
    return <img src={logo} alt="Logo" height="40" className="rc-max-w-[6rem] sm:rc-max-w-full rc-w-full rc-max-h-12 rc-rounded-md" />
  }, []);

  return (
    <Menubar className="rc-h-auto rc-rounded-none rc-p-1 rc-px-4 rc-z-[50] rc-border-0 rc-bg-brand-gradient" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div
        data-testid="clouds-top-nav-bar"
        className={`rc-w-full rc-flex rc-items-center`}
      >
        <div className="rc-flex rc-py-2 rc-w-full rc-justify-between rc-items-center ">
          <Link className="rc-text-light rc-w-auto" to={'/'}>
            {logo}
          </Link>

          <CloudsDropdown
            onCreateCloud={props.onCreateCloud}
            currentUserId={props.currentUserId}
            knownUsers={props.knownUsers}
            clouds={props.clouds}
            onCloudSelected={props.onCloudSelected}
            refreshing={props.refreshing}
            onRefresh={props.onRefresh}
            experimentalDesign={props.experimentalDesign}
          />

          <div className="rc-flex rc-justify-end rc-items-center rc-p-0 rc-gap-2">
            {/* Offline indicator with refresh button - only show in Tauri when offline */}
            {isTauri && !isOnline && (
              <div className="rc-flex rc-items-center rc-gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rc-flex rc-items-center rc-gap-1 rc-px-2 rc-py-1 rc-bg-amber-500/20 rc-rounded-full">
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="rc-text-amber-500"
                        size="sm"
                      />
                      <span className="rc-text-amber-500 rc-text-xs rc-font-medium">Offline</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    You are currently offline. Only downloaded books are available.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      icon
                      variant="ghost"
                      disabled={checkingOnline || props.refreshing}
                      onClick={handleRefreshWithOnlineCheck}
                      className="rc-w-10 rc-h-10 rc-rounded-full rc-p-0 rc-text-amber-500 hover:rc-text-amber-600"
                    >
                      <FontAwesomeIcon
                        icon={faRefresh}
                        size="lg"
                        className={checkingOnline || props.refreshing ? 'rc-animate-spin' : ''}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Check connection and refresh
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
            <Button
              icon
              variant="ghost"
              className={'rc-w-10 rc-h-10 rc-rounded-full rc-p-0 rc-text-brand-primary'}
              onClick={() =>
                props.onLayoutChange(props.layout === 'grid' ? 'list' : 'grid')
              }
            >
              {props.layout === 'grid' ? (
                <FontAwesomeIcon size="lg" icon={faBars} />
              ) : (
                <Grip className="rc-w-5 rc-h-5" />
              )}
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Avatar>
                  <AvatarFallback className="rc-bg-brand-primary rc-text-brand-secondary">
                    <FontAwesomeIcon size="lg" icon={faUser} />
                  </AvatarFallback>
                </Avatar>
              </PopoverTrigger>
              <PopoverContent
                className="rc-rounded rc-p-0 rc-z-[52] rc-w-[200px]"
                collisionPadding={16}
                sideOffset={8}
                align="end"
              >
                <div className="rc-flex rc-flex-col rc-py-4 rc-gap-2">
                  <Tooltip defaultOpen={false}>
                    <TooltipTrigger asChild>
                      <Subtle className="rc-px-4">{props.userFullName}</Subtle>
                    </TooltipTrigger>
                    <TooltipContent
                      sideOffset={8}
                      className="rc-bg-slate-100 rc-text-dark"
                    >
                      {props.userEmail}
                    </TooltipContent>
                  </Tooltip>
                  <Separator />
                  <div className="rc-flex rc-flex-col">
                    <Button
                      variant="ghost"
                      className="rc-justify-start"
                      onClick={(e) =>
                        props.onToggleSettings && props.onToggleSettings()
                      }
                    >
                      Settings
                    </Button>
                    <Button
                      variant="ghost"
                      className="rc-justify-start"
                      onClick={actions.logout}
                    >
                      Logout
                    </Button>
                  </div>
                  <Separator />
                  <Subtle className="rc-px-4">
                    Version: {packageJson.version}
                  </Subtle>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </Menubar>
  );
};

export default TopNavBar;
